import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { hubFetch } from '@core/auth/services/hub-fetch';
import { environment } from '@env/environment';
import type {
  MessageNotification,
  ProgressNotification,
} from '@shared/models/notification.model';

interface InboxResponse {
  data: (Omit<MessageNotification, 'timestamp' | 'type' | 'severity'> & {
    timestamp: string;
    category: string;
  })[];
  meta: { total: number; unread: number; snapshotAt: string };
}
interface ActivityResponse {
  data: {
    id: string;
    kind: 'analysis' | 'campaign' | 'setup';
    title: string;
    stage?: string | null;
    progress: { done: number; total: number } | null;
    syncedAt: string;
    routerLink: string;
    status: string;
    sourceName: string;
    address: string | null;
    planningId: string | null;
    createdAt: string;
    counts: { completed: number; failed: number; skipped: number } | null;
  }[];
}

/** One short line under the job title: where it stands, never a wall of counters. */
function activityBody(
  row: ActivityResponse['data'][number],
  status: ProgressNotification['status'],
): string {
  if (status !== 'in-progress') {
    const outcome =
      row.status === 'cancelled' ? 'Abgebrochen' : status === 'failed' ? 'Fehlgeschlagen' : 'Abgeschlossen';
    return row.counts
      ? `${outcome} · ${row.counts.completed} erfolgreich${row.counts.failed ? ` · ${row.counts.failed} fehlgeschlagen` : ''}`
      : outcome;
  }
  if (row.kind === 'campaign')
    return row.progress
      ? `Kampagnenlauf · ${row.progress.done} von ${row.progress.total} Analysen`
      : 'Kampagnenlauf wird gestartet';
  if (row.kind === 'setup') return 'Kampagne wird eingerichtet';
  if (row.planningId) return 'Planung wird berechnet';
  return row.progress && row.stage
    ? `Schritt ${row.progress.done + 1} von ${row.progress.total} · ${row.stage}`
    : (row.stage ?? 'Analyse läuft');
}

@Injectable({ providedIn: 'root' })
export class NotificationInboxStore {
  private readonly org = inject(CurrentOrganizationStore);
  private readonly router = inject(Router);
  private readonly url = signal(this.router.url);
  readonly items = signal<MessageNotification[]>([]);
  readonly activity = signal<ProgressNotification[]>([]);
  readonly unreadCount = signal(0);
  readonly total = signal(0);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly reconnecting = signal(false);
  readonly unreadOnly = signal(false);
  readonly organizationName = this.org.organizationName;
  readonly hasMore = computed(() => this.items().length < this.total());
  private activeOrg: string | null = null;
  /** A 403 means no membership (e.g. platform staff): stop asking until the organisation changes. */
  private deniedOrg: string | null = null;
  private generation = 0;
  private pages = 1;
  private snapshotAt = new Date().toISOString();
  private inFlight = false;
  private refreshAgain = false;
  private stream: AbortController | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    const navigation = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) this.url.set(event.urlAfterRedirects);
    });
    effect(() => {
      const url = this.url(),
        id = this.org.organizationId(),
        slug = this.org.organizationSlug();
      const routeSlug = url.match(/\/organizations\/([^/?#]+)/)?.[1];
      const signedOut =
        /^\/(login|logout|signup|forgot-password|reset-password|verify-email)([/?#]|$)/.test(
          url,
        );
      const next = signedOut || (routeSlug && routeSlug !== slug) ? null : id;
      if (next !== this.activeOrg) this.switchOrganization(next);
    });
    const focus = () => {
      void this.refresh();
    };
    const visible = () => {
      if (!document.hidden) void this.refresh();
    };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', visible);
    // Reconcile permission/resource changes even when no notification revision changed.
    const interval = setInterval(() => {
      if (!document.hidden && this.activeOrg) void this.refresh();
    }, 60_000);
    inject(DestroyRef).onDestroy(() => {
      this.generation++;
      this.stream?.abort();
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
      clearInterval(interval);
      navigation.unsubscribe();
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', visible);
    });
  }

  private switchOrganization(id: string | null) {
    this.generation++;
    this.stream?.abort();
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.activeOrg = id;
    this.deniedOrg = null;
    this.pages = 1;
    this.inFlight = false;
    this.refreshAgain = false;
    this.items.set([]);
    this.activity.set([]);
    this.unreadCount.set(0);
    this.total.set(0);
    this.error.set('');
    this.reconnecting.set(false);
    this.busy.set(false);
    if (id) {
      void this.refresh();
      void this.connect(id, this.generation);
    }
  }

  private async request<T>(
    org: string,
    path = '',
    method = 'GET',
    body?: unknown,
  ): Promise<T> {
    const response = await hubFetch(
      `${environment.apiUrl}/organizations/${encodeURIComponent(org)}/notifications${path}`,
      {
        method,
        ...(body
          ? {
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            }
          : {}),
      },
    );
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (response.status === 403) this.deniedOrg = org;
        if (this.activeOrg === org) {
          this.items.set([]);
          this.activity.set([]);
          this.unreadCount.set(0);
        }
      }
      throw new Error(
        response.status === 403
          ? 'Für diese Organisation ist keine aktive Mitgliedschaft verfügbar.'
          : 'Benachrichtigungen konnten nicht geladen werden.',
      );
    }
    return response.json() as Promise<T>;
  }

  async refresh() {
    const org = this.activeOrg,
      generation = this.generation,
      unreadOnly = this.unreadOnly();
    if (!org || org === this.deniedOrg) return;
    if (this.inFlight) {
      this.refreshAgain = true;
      return;
    }
    this.inFlight = true;
    this.busy.set(true);
    try {
      const [pages, activities] = await Promise.all([
        Promise.all(
          Array.from({ length: this.pages }, (_, page) =>
            this.request<InboxResponse>(
              org,
              `?page=${page + 1}&limit=20&unread=${this.unreadOnly()}`,
            ),
          ),
        ),
        this.request<ActivityResponse>(org, '/activity'),
      ]);
      if (generation !== this.generation || unreadOnly !== this.unreadOnly())
        return;
      const items: MessageNotification[] = [
        ...new Map(
          pages
            .flatMap((page) =>
              page.data.map((row) => ({
                ...row,
                timestamp: new Date(row.timestamp),
                type: 'message' as const,
                category: row.category,
                severity:
                  row.category === 'execution_failed'
                    ? ('error' as const)
                    : row.category.endsWith('completed')
                      ? ('success' as const)
                      : ('info' as const),
              })),
            )
            .map((item) => [item.id, item]),
        ).values(),
      ];
      const jobs: ProgressNotification[] = activities.data.map((row) => {
        const status: ProgressNotification['status'] =
          row.status === 'failed'
            ? 'failed'
            : ['completed', 'cancelled'].includes(row.status)
              ? 'complete'
              : 'in-progress';
        return {
          id: row.id,
          type: 'progress',
          title:
            row.kind === 'analysis'
              ? (row.address ?? 'Analyse')
              : row.sourceName || row.title,
          body: activityBody(row, status),
          progress: row.progress
            ? Math.round((row.progress.done / row.progress.total) * 100)
            : undefined,
          routerLink: row.routerLink,
          timestamp: new Date(row.createdAt),
          finishedAt: status === 'in-progress' ? undefined : new Date(row.syncedAt),
          read: true,
          status,
        };
      });
      if (JSON.stringify(items) !== JSON.stringify(this.items()))
        this.items.set(items);
      if (JSON.stringify(jobs) !== JSON.stringify(this.activity()))
        this.activity.set(jobs);
      this.total.set(pages[0].meta.total);
      this.unreadCount.set(pages[0].meta.unread);
      this.snapshotAt = pages[0].meta.snapshotAt;
      this.error.set('');
    } catch (e) {
      if (generation === this.generation)
        this.error.set(
          e instanceof Error ? e.message : 'Laden fehlgeschlagen.',
        );
    } finally {
      if (generation === this.generation) {
        this.inFlight = false;
        this.busy.set(false);
        if (this.refreshAgain) {
          this.refreshAgain = false;
          void this.refresh();
        }
      }
    }
  }

  async markRead(id: string, read = true) {
    await this.change(`/${encodeURIComponent(id)}/read`, 'PATCH', { read });
  }
  async markAllRead() {
    await this.change('/read-all', 'POST', { before: this.snapshotAt });
  }
  private async change(path: string, method: string, body: unknown) {
    const org = this.activeOrg;
    if (!org) return;
    try {
      await this.request(org, path, method, body);
      if (org === this.activeOrg) await this.refresh();
    } catch (e) {
      if (org === this.activeOrg)
        this.error.set(
          e instanceof Error ? e.message : 'Änderung fehlgeschlagen.',
        );
    }
  }
  filterUnread(value: boolean) {
    this.unreadOnly.set(value);
    this.pages = 1;
    void this.refresh();
  }
  loadMore() {
    this.pages++;
    void this.refresh();
  }

  private async connect(
    org: string,
    generation: number,
    failures = 0,
  ): Promise<void> {
    if (generation !== this.generation) return;
    const controller = new AbortController();
    this.stream = controller;
    let retry = true;
    try {
      const response = await hubFetch(
        `${environment.apiUrl}/organizations/${encodeURIComponent(org)}/notifications/events`,
        { headers: { Accept: 'text/event-stream' }, signal: controller.signal },
      );
      if (response.status === 401 || response.status === 403) {
        retry = false;
        if (response.status === 403) this.deniedOrg = org;
        if (generation === this.generation) {
          this.items.set([]);
          this.activity.set([]);
          this.unreadCount.set(0);
          this.error.set('Zugriff auf Benachrichtigungen nicht verfügbar.');
        }
        return;
      }
      if (!response.ok || !response.body)
        throw new Error('Verbindung unterbrochen.');
      if (generation !== this.generation) return;
      this.reconnecting.set(false);
      void this.refresh();
      const reader = response.body.getReader(),
        decoder = new TextDecoder();
      let buffer = '';
      while (!controller.signal.aborted) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        if (
          lines.some((line) => line.startsWith('data:')) &&
          generation === this.generation
        ) {
          // Only a delivered event proves the stream healthy; a stream that closes at once keeps backing off.
          failures = 0;
          void this.refresh();
        }
      }
    } catch {
      if (!controller.signal.aborted && generation === this.generation)
        this.reconnecting.set(true);
    } finally {
      if (
        !controller.signal.aborted &&
        generation === this.generation &&
        retry &&
        org !== this.deniedOrg
      ) {
        this.reconnecting.set(true);
        this.reconnectTimer = setTimeout(
          () => {
            void this.connect(org, generation, failures + 1);
          },
          Math.min(30_000, 2000 * 2 ** failures),
        );
      }
    }
  }
}
